// Motor do tutorial: primitivas de "direção" que as cenas usam para
// comandar o cursor fake, cliques com ripple, spotlight e overlays de
// dados fictícios ancorados na UI real. Cada cena é uma função async
// cancelável via AbortSignal (Próximo/Anterior/Pular abortam na hora).

export class SceneCancelled extends Error {
	constructor() {
		super("scene-cancelled");
		this.name = "SceneCancelled";
	}
}

let mountSeq = 0;

export const makeCtx = ({
	signal,
	history,
	setCursor,
	addRipple,
	setMounts,
	setSpotRect,
}) => {
	const ensure = () => {
		if (signal.aborted) throw new SceneCancelled();
	};

	const rectOf = (sel) => {
		const el = typeof sel === "string" ? document.querySelector(sel) : sel;
		if (!el || !el.getBoundingClientRect) return null;
		const r = el.getBoundingClientRect();
		return { x: r.left, y: r.top, w: r.width, h: r.height };
	};

	// target: seletor CSS, [x, y] absoluto, ou {sel, ax, ay} (âncora 0..1)
	const pointOf = (target) => {
		if (Array.isArray(target)) return { x: target[0], y: target[1] };
		let sel = target;
		let ax = 0.5;
		let ay = 0.5;
		if (target && typeof target === "object") {
			sel = target.sel;
			if (typeof target.ax === "number") ax = target.ax;
			if (typeof target.ay === "number") ay = target.ay;
		}
		const r = rectOf(sel);
		if (!r) return null;
		return { x: r.x + r.w * ax, y: r.y + r.h * ay };
	};

	const wait = (ms) =>
		new Promise((resolve, reject) => {
			ensure();
			const t = setTimeout(resolve, ms);
			signal.addEventListener(
				"abort",
				() => {
					clearTimeout(t);
					reject(new SceneCancelled());
				},
				{ once: true }
			);
		});

	// espera um seletor existir no DOM (pós-navegação)
	const waitFor = async (sel, timeout = 4000) => {
		const start = Date.now();
		while (Date.now() - start < timeout) {
			ensure();
			if (document.querySelector(sel)) return true;
			await wait(120);
		}
		return false;
	};

	const cursorTo = async (target, opts = {}) => {
		ensure();
		const { duration = 850 } = opts;
		const p = pointOf(target);
		if (!p) return;
		setCursor((c) => ({ ...c, x: p.x, y: p.y, duration, visible: true }));
		await wait(duration + 100);
	};

	const cursorHide = () => setCursor((c) => ({ ...c, visible: false }));

	// clique visual (ripple); real=true também dispara .click() no elemento —
	// usar APENAS em alvos inofensivos (abas, itens de menu)
	const click = async (target, opts = {}) => {
		ensure();
		const { real = false } = opts;
		const p = pointOf(target);
		if (p) addRipple(p);
		setCursor((c) => ({ ...c, pressing: true }));
		await wait(190);
		setCursor((c) => ({ ...c, pressing: false }));
		if (real) {
			let sel = target;
			if (target && typeof target === "object" && target.sel) sel = target.sel;
			if (typeof sel === "string") {
				const el = document.querySelector(sel);
				if (el && el.click) el.click();
			}
		}
		await wait(240);
	};

	// arrastar: leva o cursor até "from", pressiona, chama opts.onGrab()
	// (para a cena iniciar a animação do card em sincronia) e desliza até "to"
	const drag = async (from, to, opts = {}) => {
		ensure();
		const { duration = 1150 } = opts;
		await cursorTo(from, { duration: 600 });
		const p = pointOf(from);
		if (p) addRipple(p);
		setCursor((c) => ({ ...c, pressing: true }));
		await wait(240);
		if (opts.onGrab) opts.onGrab();
		const pTo = pointOf(to);
		if (pTo)
			setCursor((c) => ({
				...c,
				x: pTo.x,
				y: pTo.y,
				duration,
				visible: true,
			}));
		await wait(duration + 120);
		setCursor((c) => ({ ...c, pressing: false }));
		const p2 = pointOf(to);
		if (p2) addRipple(p2);
		await wait(260);
	};

	const navigate = async (path) => {
		ensure();
		if (history.location.pathname !== path) history.push(path);
		await wait(350);
	};

	// spotlight em um seletor (ou null para remover)
	const spotlight = (sel) => {
		if (!sel) {
			setSpotRect(null);
			return;
		}
		const r = rectOf(sel);
		if (r) setSpotRect(r);
	};

	// overlay fictício ancorado: kind = componente do DemoKit
	const mount = (kind, anchor, props = {}) => {
		ensure();
		mountSeq += 1;
		const id = `m${mountSeq}`;
		setMounts((m) => ({ ...m, [id]: { id, kind, anchor, props } }));
		return id;
	};

	const update = (id, props) => {
		setMounts((m) =>
			m[id] ? { ...m, [id]: { ...m[id], props: { ...m[id].props, ...props } } } : m
		);
	};

	const unmount = (id) => {
		setMounts((m) => {
			const n = { ...m };
			delete n[id];
			return n;
		});
	};

	const clear = () => {
		setMounts({});
		setSpotRect(null);
	};

	return {
		signal,
		history,
		wait,
		waitFor,
		rectOf,
		cursorTo,
		cursorHide,
		click,
		drag,
		navigate,
		spotlight,
		mount,
		update,
		unmount,
		clear,
	};
};
